import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCheck, faCalendarAlt, faSave, faClock, faEdit, faCheck, faTimes, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import axios from '../../Api';
import SearchableMultiSelect from '../../components/SearchableMultiSelect';

const RAISON_OPTIONS = [
  { value: 'base_de_donnee', label: 'Base de données' },
  { value: 'transcription', label: 'Transcription' },
  { value: 'remuneration', label: 'Paiement' },
  { value: 'autre', label: 'Autre' },
];

// Options for SearchableMultiSelect (id + title)
const RAISON_SELECT_OPTIONS = RAISON_OPTIONS.map(opt => ({ id: opt.value, title: opt.label }));

const ControllerAttendance = () => {
  const navigate = useNavigate();
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState(today.getDate());
  const [availableYears, setAvailableYears] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [fullDayPrice, setFullDayPrice] = useState(120); // Default: 120 MAD
  const [halfDayPrice, setHalfDayPrice] = useState(60); // Default: 60 MAD
  const [editingPrices, setEditingPrices] = useState(false);
  const [tempFullDayPrice, setTempFullDayPrice] = useState(120);
  const [tempHalfDayPrice, setTempHalfDayPrice] = useState(60);

  useEffect(() => {
    fetchAvailableYears();
    fetchAttendanceData();
  }, []);

  useEffect(() => {
    fetchAttendanceData();
  }, [selectedYear, selectedMonth, selectedDay]);

  const fetchAvailableYears = async () => {
    try {
      const response = await axios.get('/control/attendance/years');
      const years = response.data.years || [];
      if (years.length > 0) {
        setAvailableYears(years);
        // If current year not in list, add it
        if (!years.includes(today.getFullYear())) {
          setAvailableYears([today.getFullYear(), ...years].sort((a, b) => b - a));
        }
      } else {
        setAvailableYears([today.getFullYear()]);
      }
    } catch (error) {
      console.error('Error fetching available years:', error);
      setAvailableYears([today.getFullYear()]);
    }
  };

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      const response = await axios.get('/control/attendance', {
        params: { date: dateStr }
      });
      
      const data = response.data.data || [];
      
      // Initialize day_type: use confirmed if exists, otherwise auto_day_type if available, otherwise null
      const processedData = data.map(item => ({
        ...item,
        day_type: item.confirmed ? item.day_type : (item.auto_day_type || item.day_type || null),
        // Use existing price from DB if confirmed, otherwise 0 until saved
        price: item.confirmed ? (item.price || 0) : 0,
        // Track initial auto_day_type to detect changes
        initial_auto_day_type: item.auto_day_type,
        // Initialize ajustement fields (support raisons_ajustement array or legacy raison_ajustement single)
        raisons_ajustement: Array.isArray(item.raisons_ajustement) ? item.raisons_ajustement : (item.raison_ajustement ? [item.raison_ajustement] : []),
        commentaire_ajustement: item.commentaire_ajustement || null,
      }));
      
      setAttendanceData(processedData);
      
      // Set prices based on confirmed data only
      // If no confirmed data, keep defaults (120/60)
      if (data.length > 0) {
        const confirmedData = data.filter(item => item.confirmed);
        if (confirmedData.length > 0) {
          const fullDayItem = confirmedData.find(item => item.day_type === 'full_day' && item.price);
          const halfDayItem = confirmedData.find(item => item.day_type === 'half_day' && item.price);
          if (fullDayItem && fullDayItem.price > 0) {
            setFullDayPrice(fullDayItem.price);
            setTempFullDayPrice(fullDayItem.price);
          } else {
            // Reset to default if no confirmed full day price
            setFullDayPrice(120);
            setTempFullDayPrice(120);
          }
          if (halfDayItem && halfDayItem.price > 0) {
            setHalfDayPrice(halfDayItem.price);
            setTempHalfDayPrice(halfDayItem.price);
          } else {
            // Reset to default if no confirmed half day price
            setHalfDayPrice(60);
            setTempHalfDayPrice(60);
          }
        } else {
          // No confirmed data, use defaults
          setFullDayPrice(120);
          setHalfDayPrice(60);
          setTempFullDayPrice(120);
          setTempHalfDayPrice(60);
        }
      }
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const getAvailableDays = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    const daysInMonth = getDaysInMonth(selectedMonth, year);
    if (selectedDay > daysInMonth) {
      setSelectedDay(daysInMonth);
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    const daysInMonth = getDaysInMonth(month, selectedYear);
    if (selectedDay > daysInMonth) {
      setSelectedDay(daysInMonth);
    }
  };

  const handleDayTypeChange = (controllerId, dayType) => {
    setAttendanceData(prev => prev.map(item => {
      if (item.controller_id === controllerId) {
        return {
          ...item,
          day_type: dayType,
          price: 0,
          // Keep raisons and commentaire; they can be set for any row (including auto-filled)
        };
      }
      return item;
    }));
  };

  const handleRaisonsAjustementChange = (controllerId, selectedIds) => {
    setAttendanceData(prev => prev.map(item =>
      item.controller_id === controllerId ? { ...item, raisons_ajustement: selectedIds } : item
    ));
  };

  const handleCommentaireAjustementChange = (controllerId, commentaire) => {
    setAttendanceData(prev => prev.map(item => 
      item.controller_id === controllerId 
        ? { 
            ...item, 
            commentaire_ajustement: commentaire
          }
        : item
    ));
  };

  // True when day_type was manually changed from auto value or manually set (no auto)
  const isDayTypeChanged = (item) => {
    if (item.initial_auto_day_type && item.initial_auto_day_type !== item.day_type) return true;
    if (!item.initial_auto_day_type && item.day_type) return true;
    return false;
  };

  const handleSavePrices = () => {
    setFullDayPrice(tempFullDayPrice);
    setHalfDayPrice(tempHalfDayPrice);
    setEditingPrices(false);
    
    // Update prices in attendanceData based on day_type
    setAttendanceData(prev => prev.map(item => ({
      ...item,
      price: item.day_type === 'full_day' ? tempFullDayPrice : tempHalfDayPrice,
    })));
  };

  const handleCancelEditPrices = () => {
    setTempFullDayPrice(fullDayPrice);
    setTempHalfDayPrice(halfDayPrice);
    setEditingPrices(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveMessage('');
      setSaveError('');
      
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      
      // Filter to only include controllers with a selected day_type
      const attendanceToSave = attendanceData
        .filter(item => item.day_type !== null && item.day_type !== undefined)
        .map(item => {
          const raisons = item.raisons_ajustement || [];
          if (isDayTypeChanged(item) && raisons.length === 0) {
            throw new Error(`Raison(s) d'ajustement obligatoire pour ${item.controller_name} (type de journée modifié).`);
          }
          if (raisons.includes('autre') && !(item.commentaire_ajustement || '').trim()) {
            throw new Error(`Commentaire d'ajustement requis pour ${item.controller_name} (raison "autre" sélectionnée)`);
          }
          let price = item.price;
          if (!price || price === 0) {
            price = item.day_type === 'full_day' ? tempFullDayPrice : tempHalfDayPrice;
          }
          if (!price || price === 0) {
            price = item.day_type === 'full_day' ? 120 : 60;
          }
          return {
            controller_id: item.controller_id,
            submissions_count: item.submissions_count || 0,
            first_check_at: item.first_check_at,
            last_check_at: item.last_check_at,
            login_at: item.login_at,
            day_type: item.day_type,
            price: price,
            initial_auto_day_type: item.initial_auto_day_type,
            raisons_ajustement: raisons,
            commentaire_ajustement: item.commentaire_ajustement || null,
          };
        });

      if (attendanceToSave.length === 0) {
        setSaveError('Veuillez sélectionner au moins un contrôleur avec un type de journée');
        setSaving(false);
        return;
      }

      const payload = {
        date: dateStr,
        attendance: attendanceToSave,
      };

      await axios.post('/control/attendance', payload);
      
      setSaveMessage('Présence enregistrée avec succès');
      // Update prices based on saved data
      const savedFullDay = payload.attendance.find(a => a.day_type === 'full_day')?.price;
      const savedHalfDay = payload.attendance.find(a => a.day_type === 'half_day')?.price;
      if (savedFullDay && savedFullDay > 0) {
        setFullDayPrice(savedFullDay);
        setTempFullDayPrice(savedFullDay);
      }
      if (savedHalfDay && savedHalfDay > 0) {
        setHalfDayPrice(savedHalfDay);
        setTempHalfDayPrice(savedHalfDay);
      }
      setTimeout(() => {
        setSaveMessage('');
        fetchAttendanceData();
      }, 2000);
    } catch (error) {
      console.error('Error saving attendance:', error);
      // Handle validation errors
      if (error.message) {
        setSaveError(error.message);
      } else if (error.response?.data?.message) {
        setSaveError(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Handle Laravel validation errors
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        setSaveError(errorMessages.join(', '));
      } else {
        setSaveError('Erreur lors de l\'enregistrement de la présence');
      }
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleExport = async () => {
    try {
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
      const response = await axios.get(`/control/attendance/export?date=${dateStr}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `presence_controleurs.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting attendance:', error);
      let errorMessage = 'Erreur lors de l\'exportation. Veuillez réessayer.';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          if (json.message) errorMessage = json.message;
        } catch (e) { /* ignore parse errors */ }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      alert(errorMessage);
    }
  };

  // Check if selected date is today
  const isToday = () => {
    const selectedDate = new Date(selectedYear, selectedMonth - 1, selectedDay);
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return selectedDate.getTime() === todayDate.getTime();
  };

  // Check if all attendance records are confirmed
  const isAllConfirmed = () => {
    if (attendanceData.length === 0) return false;
    return attendanceData.every(item => item.confirmed);
  };

  // Determine if Save button should be shown
  const shouldShowSaveButton = () => {
    // Always show if today
    if (isToday()) return true;
    // If not today, only show if not all are confirmed
    return !isAllConfirmed();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Présence des Contrôleurs</h1>
        <p className="text-gray-600 mt-2">Gestion de la présence quotidienne des contrôleurs</p>
      </div>

      {/* Price Card */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4 flex-shrink-0">
            <FontAwesomeIcon icon={faClock} className="text-red-600 text-xl" />
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Paie Journalière</h3>
              <p className="text-sm text-gray-600">Montant par journée et demi-journée</p>
            </div>
          </div>
          
          {!editingPrices ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 lg:gap-8 flex-wrap">
              <div className="text-center sm:text-left">
                <p className="text-sm text-gray-600 mb-1">Journée complète</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-800">
                  {fullDayPrice > 0 ? `${fullDayPrice.toFixed(2)} MAD` : '120.00 MAD'}
                </p>
              </div>
              <div className="text-center sm:text-left">
                <p className="text-sm text-gray-600 mb-1">Demi-journée</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-800">
                  {halfDayPrice > 0 ? `${halfDayPrice.toFixed(2)} MAD` : '60.00 MAD'}
                </p>
              </div>
              {shouldShowSaveButton() && (
                <button
                  onClick={() => setEditingPrices(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  <FontAwesomeIcon icon={faEdit} />
                  Modifier
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-sm text-gray-600 whitespace-nowrap">Journée complète:</label>
                <input
                  type="number"
                  value={tempFullDayPrice}
                  onChange={(e) => setTempFullDayPrice(parseFloat(e.target.value) || 0)}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  step="0.01"
                  min="0"
                />
                <span className="text-sm text-gray-600">MAD</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-sm text-gray-600 whitespace-nowrap">Demi-journée:</label>
                <input
                  type="number"
                  value={tempHalfDayPrice}
                  onChange={(e) => setTempHalfDayPrice(parseFloat(e.target.value) || 0)}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                  step="0.01"
                  min="0"
                />
                <span className="text-sm text-gray-600">MAD</span>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={handleSavePrices}
                  className="flex-1 sm:flex-none px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faCheck} />
                  Confirmer
                </button>
                <button
                  onClick={handleCancelEditPrices}
                  className="flex-1 sm:flex-none px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Date Filters */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-6 mb-6">
        <div className="flex flex-col gap-4">
          {/* Date Selectors Row */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 flex-shrink-0">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-red-600" />
              <span className="text-sm font-semibold text-gray-700">Date:</span>
            </div>
            
            {/* Year Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 whitespace-nowrap">Année:</label>
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm min-w-[100px]"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Month Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 whitespace-nowrap">Mois:</label>
              <select
                value={selectedMonth}
                onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm min-w-[140px]"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <option key={month} value={month}>
                    {new Date(2000, month - 1).toLocaleString('fr-FR', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>

            {/* Day Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 whitespace-nowrap">Jour:</label>
              <select
                value={selectedDay}
                onChange={(e) => setSelectedDay(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm min-w-[80px]"
              >
                {getAvailableDays().map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Buttons Row */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:ml-auto sm:justify-end">
            {/* Export Button */}
            <button
              onClick={handleExport}
              disabled={attendanceData.length === 0 || attendanceData.filter(item => item.day_type).length === 0}
              className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              title="Exporter en Excel"
            >
              <FontAwesomeIcon icon={faFileExcel} />
              <span className="hidden sm:inline">Exporter en Excel</span>
              <span className="sm:hidden">Exporter</span>
            </button>
            
            {/* Save Button - only show if today or if not all confirmed */}
            {shouldShowSaveButton() && (
              <button
                onClick={handleSave}
                disabled={!isToday() || saving || attendanceData.length === 0 || attendanceData.filter(item => item.day_type).length === 0}
                className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                title={!isToday() ? 'Vous ne pouvez enregistrer que pour la date d\'aujourd\'hui' : ''}
              >
                <FontAwesomeIcon icon={faSave} />
                <span>{saving ? 'Enregistrement...' : 'Enregistrer'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Save Messages */}
        {saveMessage && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700">{saveMessage}</p>
          </div>
        )}

        {saveError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{saveError}</p>
          </div>
        )}
      </div>

      {/* Attendance Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      ) : attendanceData.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FontAwesomeIcon icon={faUserCheck} className="text-4xl text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">Aucun contrôleur actif trouvé</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contrôleur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Connexion
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Soumissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    1er Contrôle (matin)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    1er Contrôle (aprèm)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type de Journée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Raisons Ajustement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commentaire Ajustement
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {attendanceData.map((item) => (
                  <tr key={item.controller_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div 
                        onClick={() => navigate(`/controllers/${item.controller_id}`)}
                        className="cursor-pointer hover:text-red-600 transition-colors"
                      >
                        <div className="text-sm font-medium text-gray-900 hover:text-red-600">{item.controller_name}</div>
                        <div className="text-sm text-gray-500">Ref: {item.controller_ref}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatDateTime(item.login_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {item.submissions_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatTime(item.first_check_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatTime(item.last_check_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`day_type_${item.controller_id}`}
                            value="half_day"
                            checked={item.day_type === 'half_day'}
                            onChange={() => handleDayTypeChange(item.controller_id, 'half_day')}
                            disabled={!isToday() && item.confirmed}
                            className="w-4 h-4 text-red-600 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span className={`text-sm ${!isToday() && item.confirmed ? 'text-gray-400' : 'text-gray-700'}`}>Demi-journée</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`day_type_${item.controller_id}`}
                            value="full_day"
                            checked={item.day_type === 'full_day'}
                            onChange={() => handleDayTypeChange(item.controller_id, 'full_day')}
                            disabled={!isToday() && item.confirmed}
                            className="w-4 h-4 text-red-600 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span className={`text-sm ${!isToday() && item.confirmed ? 'text-gray-400' : 'text-gray-700'}`}>Journée complète</span>
                        </label>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {item.price && item.price > 0 
                        ? `${item.price.toFixed(2)} MAD` 
                        : <span className="text-gray-400">-</span>
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.confirmed ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Confirmé
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          En attente
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {item.day_type ? (
                        <div className="w-full min-w-[200px]">
                          <SearchableMultiSelect
                            options={RAISON_SELECT_OPTIONS}
                            selectedValues={item.raisons_ajustement || []}
                            onChange={(selectedIds) => handleRaisonsAjustementChange(item.controller_id, selectedIds)}
                            placeholder="Sélectionner des raisons..."
                            searchPlaceholder="Rechercher..."
                            disabled={!isToday() || item.confirmed}
                            maxHeight="200px"
                            showOptionMeta={false}
                            selectedCountLabel={(count) => `${count} raison${count > 1 ? 's' : ''} sélectionnée${count > 1 ? 's' : ''}`}
                          />
                          {isDayTypeChanged(item) && (item.raisons_ajustement || []).length === 0 && (
                            <p className="text-xs text-red-600 mt-1">Raison(s) d&apos;ajustement obligatoire lorsque le type de journée est modifié.</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {item.day_type ? (
                        <div className="w-full min-w-[300px]">
                          <textarea
                            value={item.commentaire_ajustement || ''}
                            onChange={(e) => handleCommentaireAjustementChange(item.controller_id, e.target.value)}
                            disabled={!isToday() && item.confirmed}
                            placeholder={(item.raisons_ajustement || []).includes('autre') ? 'Commentaire obligatoire*' : 'Commentaire (optionnel)'}
                            className={`w-full min-w-[300px] min-h-[80px] px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm disabled:opacity-50 disabled:cursor-not-allowed resize-y ${
                              (item.raisons_ajustement || []).includes('autre') && !(item.commentaire_ajustement || '').trim() ? 'border-red-300' : 'border-gray-300'
                            }`}
                            rows={4}
                          />
                          {(item.raisons_ajustement || []).includes('autre') && !(item.commentaire_ajustement || '').trim() && (
                            <p className="text-xs text-red-600 mt-1">Commentaire obligatoire</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControllerAttendance;

